import type { Web3TxMultiCalcSWR } from "@/hooks/tx-flow/useDepositCalculate";
import type { ResponseTxFees } from "@/hooks/tx-flow/useDepositStart";
import type { Permit2Actions } from "@/hooks/web3/usePermit2ApproveCallback";
import type { SupWeb3Balance, SupWeb3DepositStep, SupWeb3TxSingleVault } from "@/types";
import React, { type Dispatch, Fragment, type SetStateAction, useCallback, useEffect, useMemo, useState } from "react";

import { useRouteState } from "@/components/Web3/Common/Route/State";
import { useAmountState } from "@/components/Web3/Common/AmountState";
import SupUiLoading from "@/components/Ui/Loading";
import Web3CommonAuthenticate from "@/components/Web3/Common/Authenticate";
import Web3CommonRefundAddress from "@/components/Web3/Common/RefundAddress";
import Web3CommonSummary from "@/components/Web3/Common/Summary";
import Web3CommonSwitchNetwork from "@/components/Web3/Common/SwitchNetwork";
import Web3TxSettingsSlippageWarning from "@/components/Web3/TxSettings/SlippageWarning";
import Web3DepositApprovalERC20 from "@/components/Web3/Deposit/Approval/ERC20";
import Web3DepositApprovalSwitchPermit2 from "@/components/Web3/Deposit/Approval/SwitchPermit2";
import Web3DepositApprovalPermit2Wrapper, { Permit2SignaturePerDestination, reduceVaultListToPermitChains } from "@/components/Web3/Deposit/Approval/Permit2Wrapper";
import Web3DepositTransfer from "@/components/Web3/Deposit/Transfer";
import Web3CommonHighFeesWarning from "@/components/Web3/Common/HighFeesWarning";

import useCheckUS from "@/hooks/data/useCheckUS";
import useERC20SuperRouterAllowance from "@/hooks/web3/useERC20SuperRouterAllowance";
import useGetRefundAddress from "@/hooks/web3/useGetRefundAddress";

import { isNativeToken } from "@/utils";
import { useDynamicContext, useIsLoggedIn } from "@dynamic-labs/sdk-react-core";
import { FixedNumber } from "@ethersproject/bignumber";
import { SupButton, SupText } from "@repo/core-ui";

interface SupWeb3StepConfirmationProps {
  vaultList: SupWeb3TxSingleVault[];
  step: SupWeb3DepositStep;
  selectedBalance?: SupWeb3Balance;
  setTransferTx: Dispatch<SetStateAction<string | undefined>>;
  setStep: Dispatch<SetStateAction<SupWeb3DepositStep>>;
  totalGasCost: number | undefined;
  setTotalGasCost: Dispatch<SetStateAction<number | undefined>>;
  setPauseRequests: Dispatch<SetStateAction<boolean>>;
  setIsErc20: Dispatch<SetStateAction<boolean>>;
  simulate: boolean;
  setSimulate: Dispatch<SetStateAction<boolean>>;
  calculatedRoutes: Web3TxMultiCalcSWR;
}

export default function Web3DepositConfirmation({
  vaultList,
  setTransferTx,
  selectedBalance,
  step,
  setStep,
  totalGasCost,
  setTotalGasCost,
  setPauseRequests,
  setIsErc20,
  simulate,
  setSimulate,
  calculatedRoutes,
}: SupWeb3StepConfirmationProps) {
  const { totalAmount } = useAmountState();
  const { selectedRoutes } = useRouteState();

  const { restricted } = useCheckUS();

  const { network } = useDynamicContext();

  const isConnected = useIsLoggedIn();

  const hasSuperPoolRoute = useMemo(
    () => selectedRoutes.some(singleSelectedRoute => typeof singleSelectedRoute.selectedRoute === "object" && singleSelectedRoute.selectedRoute?.isERC20),
    [selectedRoutes]
  );

  const approvalContractAddress = useMemo(() => {
    const superPoolRoute = selectedRoutes.find(singleSelectedRoute => typeof singleSelectedRoute.selectedRoute === "object" && singleSelectedRoute.selectedRoute?.isERC20);
    if (!hasSuperPoolRoute || typeof superPoolRoute?.selectedRoute !== "object" || !superPoolRoute.selectedRoute.erc20_route_approval_target) {
      return undefined;
    }
    return superPoolRoute.selectedRoute.erc20_route_approval_target;
  }, [selectedRoutes, hasSuperPoolRoute]);

  /**
   * Switch to use the permit2 signature instead of the erc20 approval
   * It's set to true by default but can be modified by the user
   */
  const [usePermit2, setUsePermit2] = useState<boolean>(true);

  const [isApprovalRequired, setApprovalRequired] = useState<boolean>(true);

  const [discountedAmount, setDiscountedAmount] = useState<ResponseTxFees | undefined>();

  const [permit2Signature, setPermit2Signature] = useState<Permit2SignaturePerDestination[]>(() => reduceVaultListToPermitChains(vaultList));

  useEffect(() => {
    setPermit2Signature(reduceVaultListToPermitChains(vaultList));
  }, [vaultList]);

  const putPermit2Signature = useCallback((chainId: number, signature: Permit2Actions | undefined) => {
    setPermit2Signature(currentValue => currentValue.map(singleSignature => (singleSignature.chainId === chainId ? { ...singleSignature, signature } : singleSignature)));
  }, []);

  const { address: refundAddress, isLoading: isLoadingRefundAddressCheck } = useGetRefundAddress(selectedBalance?.chainId);

  useEffect(() => {
    if (selectedBalance?.chainId === 43114 || hasSuperPoolRoute) {
      setUsePermit2(false);
    }
  }, [hasSuperPoolRoute, selectedBalance?.chainId]);

  const isAmountsReady = useMemo(() => {
    const filteredAmountVaults = vaultList.filter(singleVault => !!singleVault.amount);
    return filteredAmountVaults.length === vaultList.length;
  }, [vaultList]);

  const requireRefundAddress = useMemo(() => !refundAddress, [refundAddress]);

  const requireSwitchChain = useMemo(() => (network && selectedBalance ? parseInt(`${network}`) !== parseInt(`${selectedBalance.chainId}`) : false), [network, selectedBalance]);

  const bigIntTotalAmount = useMemo(
    () => calculatedRoutes.data?.reduce((acc, singleCalculate) => (singleCalculate.in.adjusted_amount ? acc + BigInt(`${singleCalculate.in.adjusted_amount}`) : acc), 0n) ?? 0n,
    [calculatedRoutes.data]
  );

  // Approvals check
  useEffect(() => {
    if (selectedBalance && isNativeToken(selectedBalance)) {
      setApprovalRequired(false);
    }
  }, [selectedBalance]);

  const allowanceData = useERC20SuperRouterAllowance(selectedBalance, isApprovalRequired, approvalContractAddress);

  const approvedAmount = useMemo(() => {
    if (usePermit2 && permit2Signature) {
      const signedAmount = permit2Signature.reduce((acc, singleSignature) => acc + (singleSignature.signature?.amount ?? 0n), 0n);
      const closestDeadline = permit2Signature.reduce<number>((acc, singleSignature) => {
        const signatureDeadline = singleSignature.signature?.deadline ?? 0;
        return signatureDeadline > acc ? signatureDeadline : acc;
      }, 0);

      return bigIntTotalAmount === signedAmount && closestDeadline > Date.now() / 1000 ? signedAmount : 0n;
    } else {
      return allowanceData !== undefined ? (allowanceData as bigint) : undefined;
    }
  }, [usePermit2, permit2Signature, bigIntTotalAmount, allowanceData]);

  useEffect(() => {
    if (bigIntTotalAmount && approvedAmount !== undefined && selectedBalance && !isNativeToken(selectedBalance)) {
      setApprovalRequired(approvedAmount < bigIntTotalAmount);
    }
  }, [approvedAmount, bigIntTotalAmount, selectedBalance, isApprovalRequired]);

  const isLoading = useMemo(() => {
    return step === "Default" ? false : calculatedRoutes.isLoading;
  }, [calculatedRoutes.isLoading, step]);

  const isRoutesReady = useMemo(() => {
    return calculatedRoutes.data !== undefined && calculatedRoutes.data.find(singleRoute => !singleRoute.liq.ok) === undefined;
  }, [calculatedRoutes.data]);

  const isAmountError = useMemo(() => {
    const formattedAmount = !isNaN(parseFloat(totalAmount)) ? totalAmount : "0";

    if (selectedBalance) {
      if (selectedBalance.amountFixedNumber?.subUnsafe?.(FixedNumber.fromString(formattedAmount)).isNegative()) {
        return true;
      }
    }
    return false;
  }, [totalAmount, selectedBalance]);

  // superpools use effect (excluding supervaults)
  useEffect(() => {
    const isSuperVault = vaultList.some(singleVault => singleVault.is_supervault);
    setIsErc20(currentValue => (!currentValue && hasSuperPoolRoute && !isSuperVault ? true : currentValue));
  }, [hasSuperPoolRoute, setIsErc20, vaultList]);

  return (
    <div className="-mx-6 space-y-2 px-6">
      {step === "Calculate" && isAmountsReady && (
        <div className="inline-flex h-2 w-full flex-col items-center justify-center">
          <div className="h-0 self-stretch border-t-0.5 border-neutral-600" />
        </div>
      )}

      {isAmountError && (
        <div className="bg-error-50 p-4">
          <SupText variant="subtitle1" className="text-error-925">
            YOUR INPUT AMOUNTS EXCEED THE AVAILABLE FUNDING token balance
          </SupText>
        </div>
      )}

      {step === "Calculate" && isConnected && requireRefundAddress && !isLoadingRefundAddressCheck && (
        <div className="bg-warning-50 p-4">
          <SupText variant="subtitle1" className="text-warning-900">
            Non-create2 wallet is being used. Please set a refund address. You may not be able to claim vault rewards.
          </SupText>
        </div>
      )}

      {step === "Calculate" && isAmountsReady && !requireRefundAddress && (
        <Web3CommonSummary
          selectedRoutes={selectedRoutes}
          isLoading={isLoading}
          vaultList={vaultList}
          isDisabled={isAmountError}
          totalGasCost={totalGasCost}
          discountedAmount={discountedAmount}
        />
      )}

      {(isLoading || isLoadingRefundAddressCheck) && (
        <div className="mt-4 flex h-10 w-full flex-col items-center justify-between space-x-1">
          <SupUiLoading />
        </div>
      )}

      {!isLoading && !isConnected && <Web3CommonAuthenticate connectOnly />}

      {!isLoading && isConnected && !selectedBalance && (
        <SupButton type="button" onClick={() => setStep("Balance")} disabled={vaultList.length === 0} fluid>
          SELECT YOUR FUNDING TOKEN
        </SupButton>
      )}

      {!isLoading && selectedBalance && isConnected && step === "Calculate" && requireRefundAddress && !isLoadingRefundAddressCheck && <Web3CommonRefundAddress />}

      {!isLoading && selectedBalance && isConnected && step === "Calculate" && isRoutesReady && (
        <>
          {/* High-slippage warning */}
          <Web3TxSettingsSlippageWarning />

          {/* High fees warning */}
          <Web3CommonHighFeesWarning totalGasCost={totalGasCost} discountedAmount={discountedAmount} />

          {requireSwitchChain && <Web3CommonSwitchNetwork targetChainId={selectedBalance.chainId as number} />}

          {!requireSwitchChain && isAmountsReady && !requireRefundAddress && !isAmountError && (
            <Fragment>
              {/* Restricted for the US-based users */}
              {restricted && (
                <SupText variant="detail" className="space-y-2 border-[.5px] border-[#BA002A]/24 bg-[#BA002A]/8 p-2 text-[11px] text-[#85001E]" as="div">
                  <span>
                    Depositing on this interface is not available to residents of the United States and its territories (Puerto Rico, Guam, American Samoa, U.S. Virgin Islands and
                    the Mariana Islands).
                  </span>
                  <a href="https://superformlabs.org/terms-of-service" target="_blank" className="inline-flex w-full underline hover:no-underline">
                    Please see our Terms of Service for more information
                  </a>
                </SupText>
              )}

              {/* Switcher for the permit2 / erc-20 approval method */}
              {isApprovalRequired && !hasSuperPoolRoute && (
                <Web3DepositApprovalSwitchPermit2 selectedBalance={selectedBalance} usePermit2={usePermit2} setUsePermit2={setUsePermit2} />
              )}

              {/* When all conditions to proceed are met, user should either approve or transfer */}
              {isApprovalRequired ? (
                <Fragment>
                  {usePermit2 ? (
                    <Web3DepositApprovalPermit2Wrapper
                      calculatedRoutes={calculatedRoutes}
                      permit2Signature={permit2Signature}
                      setPauseRequests={setPauseRequests}
                      setApprovalRequired={setApprovalRequired}
                      putPermit2Signature={putPermit2Signature}
                      disabled={restricted}
                      selectedBalance={selectedBalance}
                    />
                  ) : (
                    <Web3DepositApprovalERC20
                      calculatedRoutes={calculatedRoutes}
                      setApprovalRequired={setApprovalRequired}
                      selectedBalance={selectedBalance}
                      approvedAmount={approvedAmount}
                      setPauseRequests={setPauseRequests}
                      disabled={restricted}
                      contractAddress={approvalContractAddress}
                    />
                  )}
                </Fragment>
              ) : (
                <Web3DepositTransfer
                  simulate={simulate}
                  setSimulate={setSimulate}
                  setTotalGasCost={setTotalGasCost}
                  selectedBalance={selectedBalance}
                  vaultList={vaultList}
                  setStep={setStep}
                  setTransferTx={setTransferTx}
                  setPauseRequests={setPauseRequests}
                  disabled={restricted}
                  permit2Signature={permit2Signature}
                  setDiscountedAmount={setDiscountedAmount}
                  calculatedRoutes={calculatedRoutes}
                  selectedRoutes={selectedRoutes}
                />
              )}
            </Fragment>
          )}
        </>
      )}
    </div>
  );
}
